import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createStockRequest,
  getIncomingPendingStockRequests,
  getStockRequests,
} from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import InlineLoading from '../components/InlineLoading';

const STATUS_META = {
  draft: { label: 'Borrador', tone: 'bg-gray-100 text-gray-700 border-gray-200' },
  sent: { label: 'Enviada', tone: 'bg-amber-100 text-amber-700 border-amber-200' },
  fulfilled: { label: 'Completada', tone: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const StockRequestsListPage = () => {
  const navigate = useNavigate();
  const { branchId: routeBranchId } = useParams();
  const branchId = routeBranchId || localStorage.getItem('selectedBranchId');
  const { user } = useCurrentUser();

  const canReadStock = hasPermission(user, 'stock.read');
  const canCreateStock = hasPermission(user, 'stock.create');

  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [isMainBranch, setIsMainBranch] = useState(false);

  const fetchLists = useCallback(async () => {
    if (!branchId || !canReadStock) return;
    setLoading(true);
    setError('');
    try {
      const params = { pageSize: 0 };
      if (statusFilter) params.status = statusFilter;
      const data = isMainBranch
        ? await getIncomingPendingStockRequests(branchId, params)
        : await getStockRequests(branchId, params);
      const normalized = normalizePaginatedResponse(data);
      setLists(normalized.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'No se pudieron cargar las solicitudes.');
    } finally {
      setLoading(false);
    }
  }, [branchId, canReadStock, statusFilter, isMainBranch]);

  useEffect(() => {
    fetchLists();
  }, [fetchLists, refreshKey]);

  useEffect(() => {
    if (!branchId) return;
    let mounted = true;
    const detectMainBranch = async () => {
      try {
        await getIncomingPendingStockRequests(branchId, { pageSize: 1 });
        if (mounted) setIsMainBranch(true);
      } catch (err) {
        if (mounted) setIsMainBranch(false);
      }
    };
    detectMainBranch();
    return () => {
      mounted = false;
    };
  }, [branchId]);

  const handleCreate = async () => {
    if (!canCreateStock || isMainBranch || !branchId) return;
    setCreating(true);
    setCreateError('');
    try {
      const response = await createStockRequest(branchId, {});
      const nextId = response?.requestList?.id;
      if (nextId) {
        navigate(`/branches/${branchId}/stock-requests/${nextId}?add=1`);
        return;
      }
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      setCreateError(err?.response?.data?.message || 'No se pudo crear la solicitud.');
    } finally {
      setCreating(false);
    }
  };

  const renderedLists = useMemo(() => lists || [], [lists]);

  if (!canReadStock) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg border p-4 text-sm text-gray-600">
          No tienes permisos para ver solicitudes.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Solicitudes a matriz</h1>
            <p className="text-sm text-gray-600">
              Gestiona listas de productos solicitados a la sucursal principal.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setRefreshKey((prev) => prev + 1)}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 text-sm"
          >
            Refrescar
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border border-gray-200 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Estado</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Todos</option>
                <option value="draft">Borrador</option>
                <option value="sent">Enviada</option>
                <option value="fulfilled">Completada</option>
              </select>
            </div>
            {!isMainBranch && canCreateStock && (
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating}
                className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-60"
              >
                Nueva solicitud
              </button>
            )}
          </div>

          {createError && <p className="text-sm text-red-600">{createError}</p>}
          {isMainBranch && (
            <p className="text-xs text-gray-500">
              Estás en la sucursal principal: la creación de solicitudes está deshabilitada aquí.
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          {loading ? (
            <InlineLoading label="Cargando solicitudes" />
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : renderedLists.length === 0 ? (
            <p className="text-sm text-gray-600">No hay solicitudes para mostrar.</p>
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {renderedLists.map((list) => {
                  const meta = STATUS_META[list.status] || STATUS_META.draft;
                  return (
                    <button
                      key={list.id}
                      type="button"
                      className="w-full text-left rounded-lg border border-gray-200 p-3 bg-gray-50"
                      onClick={() => navigate(`/branches/${branchId}/stock-requests/${list.id}`)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <p className="font-medium text-gray-800">{list.name || `Solicitud #${list.id}`}</p>
                        <span className={`inline-flex px-2 py-1 rounded-full border text-xs ${meta.tone}`}>
                          {meta.label}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600">
                        <div className="rounded bg-white px-2 py-1.5">
                          <p className="text-[11px] text-gray-500">Items</p>
                          <p className="font-semibold text-gray-800">{list.Items?.length || 0}</p>
                        </div>
                        <div className="rounded bg-white px-2 py-1.5">
                          <p className="text-[11px] text-gray-500">Solicitado</p>
                          <p className="font-semibold text-gray-800">
                            {Number(list.progress?.requestedTotal || 0).toFixed(2)}
                          </p>
                        </div>
                        <div className="rounded bg-white px-2 py-1.5">
                          <p className="text-[11px] text-gray-500">Pendiente</p>
                          <p className="font-semibold text-gray-800">
                            {Number(list.progress?.remainingTotal || 0).toFixed(2)}
                          </p>
                        </div>
                        <div className="rounded bg-white px-2 py-1.5">
                          <p className="text-[11px] text-gray-500">Enviada</p>
                          <p className="font-semibold text-gray-800">{formatDateTime(list.sentAt)}</p>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">Creada: {formatDateTime(list.createdAt)}</p>
                    </button>
                  );
                })}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Nombre</th>
                      <th className="px-3 py-2 text-left">Estado</th>
                      <th className="px-3 py-2 text-right">Items</th>
                      <th className="px-3 py-2 text-right">Solicitado</th>
                      <th className="px-3 py-2 text-right">Pendiente</th>
                      <th className="px-3 py-2 text-left">Creada</th>
                      <th className="px-3 py-2 text-left">Enviada</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {renderedLists.map((list) => {
                      const meta = STATUS_META[list.status] || STATUS_META.draft;
                      return (
                        <tr
                          key={list.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(`/branches/${branchId}/stock-requests/${list.id}`)}
                        >
                          <td className="px-3 py-2 font-medium text-gray-800">{list.name || `Solicitud #${list.id}`}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex px-2 py-1 rounded-full border text-xs ${meta.tone}`}>
                              {meta.label}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">{list.Items?.length || 0}</td>
                          <td className="px-3 py-2 text-right">{Number(list.progress?.requestedTotal || 0).toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">{Number(list.progress?.remainingTotal || 0).toFixed(2)}</td>
                          <td className="px-3 py-2">{formatDateTime(list.createdAt)}</td>
                          <td className="px-3 py-2">{formatDateTime(list.sentAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockRequestsListPage;
